<script>
	import { base } from '$app/paths';
	import { onMount } from 'svelte';
	import SeoHead from '../../common/SeoHead.svelte';
	import ProductNav from './ProductNav.svelte';
	import links from './publicDocLinks.json';
	import './publicDocs.css';

	export let document;
	let navigationOpen = false;
	onMount(() => {
		const wide = window.matchMedia('(min-width: 1001px)');
		const updateNavigation = () => (navigationOpen = wide.matches);
		updateNavigation();
		wide.addEventListener('change', updateNavigation);
		return () => wide.removeEventListener('change', updateNavigation);
	});
	$: isManual = document.section === 'user-guide';
	$: siblings = links.filter((link) => link.section === document.section);
	$: currentIndex = siblings.findIndex((link) => link.path === document.path);
	$: previous = siblings[currentIndex - 1];
	$: next = siblings[currentIndex + 1];
	$: canonical = `https://tools.d-cine.net${base}${document.path}`;
	$: breadcrumbs = [
		{ name: 'DCP Cine Player', item: `https://tools.d-cine.net${base}/dcp-cine-player/` },
		...(isManual && document.slug !== 'index'
			? [{ name: 'User Manual', item: `https://tools.d-cine.net${base}/dcp-cine-player/manual/` }]
			: []),
		{ name: document.label, item: canonical }
	];
	$: schema = JSON.stringify({
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': isManual ? 'TechArticle' : 'WebPage',
				headline: document.title,
				name: document.title,
				description: document.description,
				url: canonical,
				inLanguage: 'en',
				image: `https://tools.d-cine.net${base}${document.image}`,
				...(isManual
					? { dateModified: document.updated, author: { '@type': 'Organization', name: 'D-Cine' } }
					: {})
			},
			{
				'@type': 'BreadcrumbList',
				itemListElement: breadcrumbs.map((crumb, index) => ({
					'@type': 'ListItem',
					position: index + 1,
					...crumb
				}))
			}
		]
	}).replace(/</g, '\\u003c');
</script>

<SeoHead
	title={document.title}
	description={document.description}
	path={document.path}
	image={document.image}
	type={isManual ? 'article' : 'website'}
/>
<svelte:head>{@html `<script type="application/ld+json">${schema}</script>`}</svelte:head>

<div class="player-public-docs">
	<ProductNav />
	<nav class="doc-breadcrumbs" aria-label="Breadcrumb">
		<a href={`${base}/dcp-cine-player/`}>DCP Cine Player</a>
		<span aria-hidden="true">/</span>
		{#if isManual && document.slug !== 'index'}
			<a href={`${base}/dcp-cine-player/manual/`}>User Manual</a><span aria-hidden="true">/</span>
		{/if}
		<span aria-current="page">{document.label}</span>
	</nav>
	<div class:manual-layout={isManual} class="doc-layout">
		{#if isManual}
			<aside class="chapter-nav">
				<details class="chapter-menu" bind:open={navigationOpen}>
					<summary>Manual chapters and contents</summary>
					<nav aria-label="Manual chapters">
						<strong>User Manual</strong>
						{#each siblings as link}
							<a
								href={`${base}${link.path}`}
								aria-current={link.path === document.path ? 'page' : undefined}>{link.label}</a
							>
						{/each}
					</nav>
					{#if document.toc.length > 1}
						<details class="doc-toc" open>
							<summary>On this page</summary>
							<nav aria-label="On this page">
								{#each document.toc as heading}<a href={`#${heading.id}`}>{heading.title}</a>{/each}
							</nav>
						</details>
					{/if}
				</details>
			</aside>
		{/if}
		<div class="doc-main">
			<slot name="actions" />
			<!-- Reviewed, build-time public documentation; never user-provided runtime HTML. -->
			<article class="doc-content" class:manual-content={isManual}>
				{@html document.html.replaceAll('__APP_BASE__', base)}
			</article>
			<nav class="doc-paging" aria-label="Adjacent pages">
				{#if previous}<a href={`${base}${previous.path}`}
						><i class="bx bx-left-arrow-alt" aria-hidden="true"></i>{previous.label}</a
					>{/if}
				{#if next}<a class="next" href={`${base}${next.path}`}
						>{next.label}<i class="bx bx-right-arrow-alt" aria-hidden="true"></i></a
					>{/if}
			</nav>
			<p class="doc-edition">
				Written by James Gardiner · Documentation updated {document.updated}.
				For more information visit <a href="https://tools.d-cine.net">tools.d-cine.net</a>.
				Check procedures against the installed release and venue policy.
			</p>
		</div>
	</div>
</div>
